import { useEffect, useState } from "react";
import { queryReport } from "../api/newReportingClient";
import { MetricKey, ReportResponse } from "../api/types";
import { ReportFilterValue } from "../components/NewReportFilters";
import { useReportQuery } from "./useReportQuery";
import { ReportQueryState } from "./useReportQuery";
import { toQueryParams } from "./filters";

// Daily financial columns, in display order — mirrors the old Financial Report's core columns.
export const DAILY_METRICS: MetricKey[] = [
  "Gross",
  "NetSales",
  "Tax",
  "Tip",
  "Surcharge",
  "Discount",
  "Refunds",
  "OrderCount",
  "TransactionCount",
];

// Safety cap on the per-day EventSummary fan-out (one request per active day).
const MAX_DAYS = 92;

export interface FinancialByDayState extends ReportQueryState {
  note: string | null;
}

// Builds a per-DAY financial table that mirrors the old daily Financial Report.
//
// WHY THIS SHAPE: the engine's SalesByDay report only exposes {OrderCount, Gross}
// (see ReportCatalog) — it does NOT carry Net/Tax/Tip/Surcharge/Discount/Refunds/Transactions.
// Rather than change the engine (flagged separately), we compose the full set client-side:
//   1. SalesByDay (1 call) gives the active business-day axis + Orders + Gross.
//   2. For a single selected event, fan out EventSummary per active day (from=to=day).
//      Per-day ResolveWindow(d,d,zone) == exactly that 5 AM-local business day, so the
//      per-day calls are day-aligned with the SalesByDay buckets. We read each response's
//      event ROW (not totals) — the row is correct even though EventSummary's TransactionCount
//      *total* has a scoping bug (the row is a per-event dictionary lookup).
// Totals are summed from the visible day rows (correct, and independent of that engine bug).
export function useFinancialByDay(filters: ReportFilterValue): FinancialByDayState {
  // Spine: the day axis (active business days) + Orders/Gross. Always available, even with no event.
  const spine = useReportQuery(toQueryParams("SalesByDay", filters));

  const [enrich, setEnrich] = useState<{
    byDay: Record<string, Partial<Record<MetricKey, number>>>;
    loading: boolean;
    error: string | null;
  }>({ byDay: {}, loading: false, error: null });

  const eventId = filters.eventId;
  const dayKeys =
    spine.data?.rows.map((r) => r.key).filter((k): k is string => !!k) ?? [];
  const dayKeysSig = JSON.stringify(dayKeys);

  useEffect(() => {
    // Enrich only when a single event is selected and the active-day count is within the cap.
    if (!eventId || dayKeys.length === 0 || dayKeys.length > MAX_DAYS) {
      setEnrich({ byDay: {}, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setEnrich((s) => ({ ...s, loading: true, error: null }));

    Promise.all(
      dayKeys.map((day) =>
        queryReport({ reportType: "EventSummary", from: day, to: day, eventId }).then(
          (resp) => ({ day, values: resp.rows[0]?.values ?? {} })
        )
      )
    )
      .then((results) => {
        if (cancelled) return;
        const byDay: Record<string, Partial<Record<MetricKey, number>>> = {};
        for (const r of results) byDay[r.day] = r.values;
        setEnrich({ byDay, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEnrich({
          byDay: {},
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load daily financials",
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, dayKeysSig]);

  if (!spine.data) {
    return { data: null, loading: spine.loading, error: spine.error, note: null };
  }

  const hasEvent = !!eventId;
  const tooMany = hasEvent && dayKeys.length > MAX_DAYS;
  const enriched = hasEvent && !tooMany && Object.keys(enrich.byDay).length > 0;

  const metrics: MetricKey[] = enriched ? DAILY_METRICS : ["Gross", "OrderCount"];

  const rows = spine.data.rows.map((r) => {
    const base: Partial<Record<MetricKey, number>> = { ...r.values };
    const extra = enriched && r.key ? enrich.byDay[r.key] : undefined;
    return {
      key: r.key,
      label: r.key ?? r.label, // yyyy-MM-dd; rendered via formatDay in the grid
      values: extra ? { ...base, ...extra } : base,
      group: null,
    };
  });

  // Total row summed from the visible day rows — correct, and avoids the engine's
  // scoped-TransactionCount totals bug (EventSummaryAsync.txnByEvent is unscoped).
  const totals: Partial<Record<MetricKey, number>> = {};
  for (const m of metrics) {
    totals[m] = rows.reduce((sum, row) => sum + (row.values[m] ?? 0), 0);
  }

  const data: ReportResponse = {
    ...spine.data,
    type: "SalesByDay",
    metrics,
    rows,
    totals,
  };

  const note = !hasEvent
    ? "Showing Orders + Gross only. Select a single event to load the full daily breakdown (Net, Tax, Tips, Service Fee, Discounts, Refunds, Transactions)."
    : tooMany
    ? `The selected range has ${dayKeys.length} active days (cap ${MAX_DAYS}). Narrow the range to load the full per-day financials — showing Orders + Gross.`
    : null;

  return {
    data,
    loading: spine.loading || enrich.loading,
    error: spine.error ?? enrich.error,
    note,
  };
}
